/**
 * Admin Mikrobiom Tab — 3 Kategorien + Bulk Upload + Gesamtanalyse
 */

import { useState, useRef, useCallback } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2,
  Upload,
  RefreshCw,
  Trash2,
  FileText,
  Download,
  ChevronDown,
  ChevronUp,
  Search,
  Microscope,
  FlaskConical,
  Table2,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  ArrowLeft,
  Sparkles,
  Activity,
  BarChart3,
  Brain,
} from "lucide-react";
import { toast } from "sonner";

// ─── Theme tokens ────────────────────────────────────────────
const T = {
  bg: "#f7f4ef",
  white: "#ffffff",
  dark: "#2d3a1e",
  olive: "#6b7f4e",
  oliveLight: "#f0f3eb",
  saffron: "#c8a45e",
  saffronLight: "#fdf8ee",
  mid: "#5a6b4a",
  muted: "#8a9680",
  border: "#d4dcc4",
};

// ─── File type config (only 3 Mikrobiom types) ──────────────
const FILE_TYPE_CONFIG: Record<
  string,
  {
    label: string;
    shortLabel: string;
    icon: typeof FlaskConical;
    color: string;
    bgColor: string;
    accept: string;
    description: string;
  }
> = {
  microbiome_biomarker_pdf: {
    label: "Mikrobiom-Biomarker (PDF-Scores)",
    shortLabel: "Biomarker",
    icon: FlaskConical,
    color: "#8a6b4a",
    bgColor: "#f7f0e8",
    accept: ".pdf,.jpg,.jpeg,.png",
    description: "PDF mit Biomarker-Scores und Referenzwerten",
  },
  microbiome_raw_xlsx: {
    label: "Mikrobiom-Rohdaten (XLSX)",
    shortLabel: "Rohdaten",
    icon: Table2,
    color: "#6b4a8a",
    bgColor: "#f3eef7",
    accept: ".xlsx,.xls,.csv",
    description: "Excel-Datei mit Bakterien-Abundanzen",
  },
  microbiome_report_pdf: {
    label: "Mikrobiom-Bericht (PDF)",
    shortLabel: "Bericht",
    icon: Microscope,
    color: "#2d8a6b",
    bgColor: "#eef7f3",
    accept: ".pdf,.jpg,.jpeg,.png",
    description: "Mikrobiom-Ergebnisbericht mit Scores, Enterotyp und Pathogenen",
  },
};

// ─── Auto-detect file type from filename/mime ───────────────
function autoDetectFileType(file: File): string {
  const name = file.name.toLowerCase();
  const ext = name.split(".").pop() || "";

  // Excel → Rohdaten
  if (["xlsx", "xls", "csv"].includes(ext)) {
    return "microbiome_raw_xlsx";
  }

  // PDF/Image → try to distinguish biomarker vs report
  // If filename contains hints, use them
  if (name.includes("biomarker") || name.includes("score")) {
    return "microbiome_biomarker_pdf";
  }
  if (name.includes("bericht") || name.includes("report") || name.includes("ergebnis")) {
    return "microbiome_report_pdf";
  }

  // Default: report (most common upload)
  return "microbiome_report_pdf";
}

// ─── Status badge ────────────────────────────────────────────
function StatusBadge({ status }: { status: string | null }) {
  if (!status) return null;
  const config: Record<string, { label: string; className: string; icon: typeof CheckCircle2 }> = {
    parsed: { label: "geparst", className: "border-green-500/40 text-green-600 bg-green-500/10", icon: CheckCircle2 },
    parsing: { label: "wird geparst...", className: "border-amber-500/40 text-amber-600 bg-amber-500/10", icon: Clock },
    uploaded: { label: "hochgeladen", className: "border-blue-500/40 text-blue-600 bg-blue-500/10", icon: Upload },
    skipped: { label: "übersprungen", className: "border-gray-500/40 text-gray-600 bg-gray-500/10", icon: AlertTriangle },
    error: { label: "Fehler", className: "border-red-500/40 text-red-600 bg-red-500/10", icon: XCircle },
  };
  const c = config[status] || config.uploaded;
  return (
    <Badge variant="outline" className={`text-[10px] py-0 gap-1 ${c.className}`}>
      <c.icon className="w-3 h-3" />
      {c.label}
    </Badge>
  );
}

// ─── Bulk Upload Dialog ─────────────────────────────────────
function BulkUploadDialog({
  open,
  onOpenChange,
  clientId,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  onSuccess: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, { status: string; message: string }>>({});
  const [isDragOver, setIsDragOver] = useState(false);

  const addFiles = useCallback((files: FileList | File[]) => {
    const arr = Array.from(files);
    setSelectedFiles((prev) => [...prev, ...arr]);
  }, []);

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles]
  );

  const handleUploadAll = async () => {
    if (selectedFiles.length === 0 || isUploading) return;
    setIsUploading(true);
    const progress: Record<string, { status: string; message: string }> = {};

    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const key = `${i}-${file.name}`;
      progress[key] = { status: "uploading", message: "Wird hochgeladen..." };
      setUploadProgress({ ...progress });

      try {
        const detectedType = autoDetectFileType(file);

        // Read file as base64
        const buffer = await file.arrayBuffer();
        const base64 = btoa(
          new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), "")
        );

        // Use tRPC mutation directly via fetch (since we need base64)
        const formData = new FormData();
        formData.append("file", file);
        formData.append("clientId", clientId);
        formData.append("fileType", detectedType);

        const response = await fetch("/api/bio/upload", {
          method: "POST",
          body: formData,
          credentials: "include",
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(`HTTP ${response.status}: ${text}`);
        }

        const result = await response.json();

        if (result.success) {
          const config = FILE_TYPE_CONFIG[detectedType];
          progress[key] = {
            status: "success",
            message: `✓ ${config?.shortLabel || detectedType}: ${result.parsedSummary || "hochgeladen"}`,
          };
          successCount++;
        } else {
          progress[key] = { status: "error", message: `✗ ${result.error || "Fehler"}` };
          errorCount++;
        }
      } catch (err: any) {
        progress[key] = { status: "error", message: `✗ ${err.message}` };
        errorCount++;
      }
      setUploadProgress({ ...progress });
    }

    setIsUploading(false);

    if (successCount > 0) {
      toast.success(`${successCount} Datei(en) erfolgreich hochgeladen und geparst`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} Datei(en) fehlgeschlagen`);
    }

    onSuccess();

    // Close after short delay
    setTimeout(() => {
      onOpenChange(false);
      setSelectedFiles([]);
      setUploadProgress({});
    }, 1500);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!isUploading) { onOpenChange(v); if (!v) { setSelectedFiles([]); setUploadProgress({}); } } }}>
      <DialogContent className="bg-white border-[#d4dcc4] text-[#2d3a1e] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: T.dark, fontFamily: "'Playfair Display', serif" }}>
            <Upload className="w-5 h-5" style={{ color: T.olive }} />
            Mikrobiom-Daten hochladen
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm" style={{ color: T.mid }}>
            Lade Mikrobiom-Berichte hoch (PDF, Excel, CSV). Alle Berichte werden sicher gespeichert, automatisch erkannt und geparst.
          </p>

          {/* Supported providers */}
          <div className="p-3 rounded-lg" style={{ background: T.saffronLight }}>
            <p className="text-xs font-medium" style={{ color: T.saffron }}>
              ℹ Unterstützte Anbieter
            </p>
            <p className="text-xs mt-1" style={{ color: T.mid }}>
              BIOMES, myBioma, Atlas Biomed, Viome und andere Mikrobiom-Tests. PDF, Excel oder CSV.
            </p>
          </div>

          {/* Drop zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
              isDragOver ? "border-[#6b7f4e] bg-[#6b7f4e]/5" : ""
            }`}
            style={{ borderColor: isDragOver ? T.olive : T.border, background: isDragOver ? `${T.olive}08` : T.bg }}
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
          >
            <Upload className="w-8 h-8 mx-auto mb-2" style={{ color: isDragOver ? T.olive : T.muted }} />
            <p className="text-sm font-medium" style={{ color: isDragOver ? T.olive : T.mid }}>
              Dateien hierher ziehen oder klicken
            </p>
            <p className="text-xs mt-1" style={{ color: T.muted }}>
              Mehrere Dateien gleichzeitig möglich · PDF, Excel, CSV, JPG, PNG · Max. 16 MB pro Datei
            </p>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.xlsx,.xls,.csv,.jpg,.jpeg,.png"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              e.target.value = "";
            }}
          />

          {/* Selected files list */}
          {selectedFiles.length > 0 && (
            <div className="space-y-1.5 max-h-48 overflow-auto">
              {selectedFiles.map((file, i) => {
                const key = `${i}-${file.name}`;
                const prog = uploadProgress[key];
                const detected = autoDetectFileType(file);
                const config = FILE_TYPE_CONFIG[detected];

                return (
                  <div
                    key={key}
                    className="flex items-center gap-2 p-2 rounded-lg border text-sm"
                    style={{
                      borderColor: prog?.status === "error" ? "#ef444440" : prog?.status === "success" ? "#22c55e40" : T.border,
                      background: prog?.status === "error" ? "#fef2f2" : prog?.status === "success" ? "#f0fdf4" : T.bg,
                    }}
                  >
                    <Badge
                      variant="outline"
                      className="text-[9px] py-0 px-1.5 shrink-0"
                      style={{ color: config?.color, borderColor: `${config?.color}40`, background: `${config?.color}10` }}
                    >
                      {config?.shortLabel || "?"}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs truncate" style={{ color: T.dark }}>{file.name}</p>
                      {prog ? (
                        <p className="text-[10px]" style={{ color: prog.status === "error" ? "#ef4444" : prog.status === "success" ? "#22c55e" : T.muted }}>
                          {prog.message}
                        </p>
                      ) : (
                        <p className="text-[10px]" style={{ color: T.muted }}>
                          {(file.size / 1024 / 1024).toFixed(2)} MB · Auto-erkannt: {config?.shortLabel}
                        </p>
                      )}
                    </div>
                    {!isUploading && !prog && (
                      <button
                        onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                        className="p-1 rounded hover:bg-red-50"
                      >
                        <XCircle className="w-3.5 h-3.5 text-red-400" />
                      </button>
                    )}
                    {prog?.status === "uploading" && (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: T.olive }} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => { onOpenChange(false); setSelectedFiles([]); setUploadProgress({}); }}
            disabled={isUploading}
            className="border-[#d4dcc4] text-[#5a6b4a]"
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleUploadAll}
            disabled={selectedFiles.length === 0 || isUploading}
            className="bg-[#6b7f4e] text-white hover:bg-[#5a6e3f]"
          >
            {isUploading ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Wird verarbeitet...</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" /> {selectedFiles.length} Datei(en) hochladen</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Single Upload Dialog (for replacing specific type) ─────
function SingleUploadDialog({
  open,
  onOpenChange,
  clientId,
  fileType,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  fileType: string;
  onSuccess: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const config = FILE_TYPE_CONFIG[fileType];

  const handleUpload = async () => {
    if (!selectedFile || isUploading) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("clientId", clientId);
      formData.append("fileType", fileType);

      const response = await fetch("/api/bio/upload", {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`HTTP ${response.status}: ${text}`);
      }

      const result = await response.json();
      if (result.success) {
        toast.success(`${config?.label}: ${result.parsedSummary || "Hochgeladen"}`);
        onSuccess();
        onOpenChange(false);
        setSelectedFile(null);
      } else {
        toast.error(result.error || "Upload fehlgeschlagen");
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!isUploading) { onOpenChange(v); if (!v) setSelectedFile(null); } }}>
      <DialogContent className="bg-white border-[#d4dcc4] text-[#2d3a1e] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: T.dark, fontFamily: "'Playfair Display', serif" }}>
            <Upload className="w-5 h-5" style={{ color: config?.color }} />
            {config?.label || "Datei"} hochladen
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm" style={{ color: T.mid }}>{config?.description}</p>

          <div
            className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors hover:border-[#6b7f4e]"
            style={{ borderColor: T.border, background: T.bg }}
            onClick={() => fileRef.current?.click()}
          >
            {selectedFile ? (
              <div className="space-y-2">
                <FileText className="w-8 h-8 mx-auto" style={{ color: config?.color }} />
                <p className="text-sm font-medium" style={{ color: T.dark }}>{selectedFile.name}</p>
                <p className="text-xs" style={{ color: T.muted }}>{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="w-8 h-8 mx-auto" style={{ color: T.muted }} />
                <p className="text-sm" style={{ color: T.mid }}>Klicke hier oder ziehe eine Datei hierher</p>
                <p className="text-xs" style={{ color: T.muted }}>Akzeptiert: {config?.accept}</p>
              </div>
            )}
          </div>

          <input
            ref={fileRef}
            type="file"
            accept={config?.accept}
            className="hidden"
            onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
          />
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { onOpenChange(false); setSelectedFile(null); }} className="border-[#d4dcc4] text-[#5a6b4a]">
            Abbrechen
          </Button>
          <Button onClick={handleUpload} disabled={!selectedFile || isUploading} className="bg-[#6b7f4e] text-white hover:bg-[#5a6e3f]">
            {isUploading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Wird geparst...</> : <><Upload className="w-4 h-4 mr-2" /> Hochladen & Parsen</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Data Source Card ────────────────────────────────────────
function DataSourceCard({
  card,
  onUpload,
  onReparse,
  onExpand,
  expanded,
}: {
  card: {
    fileType: string;
    label: string;
    icon: string;
    hasData: boolean;
    status: string | null;
    parsedCount: number;
    parsedSummary: string | null;
    fileCount: number;
    latestFileName: string | null;
    latestFileId: number | null;
  };
  onUpload: () => void;
  onReparse: () => void;
  onExpand: () => void;
  expanded: boolean;
}) {
  const config = FILE_TYPE_CONFIG[card.fileType];
  if (!config) return null;
  const Icon = config.icon;

  return (
    <div
      className="rounded-lg border p-4 transition-all"
      style={{
        borderColor: card.hasData && card.status === "parsed" ? `${config.color}40` : T.border,
        background: card.hasData ? config.bgColor : T.white,
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="p-2 rounded-lg" style={{ background: `${config.color}15` }}>
            <Icon className="w-5 h-5" style={{ color: config.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-sm font-semibold" style={{ color: T.dark }}>{config.label}</h4>
              {card.hasData && <StatusBadge status={card.status} />}
            </div>
            <p className="text-xs mt-0.5 truncate" style={{ color: T.muted }}>
              {card.hasData
                ? card.parsedSummary || card.latestFileName || "Datei vorhanden"
                : `Keine ${config.shortLabel}-Datei hochgeladen`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {card.hasData && card.status === "parsed" && (
            <button
              onClick={onReparse}
              className="flex items-center gap-1 text-xs px-2 py-1.5 rounded-md border transition-colors hover:bg-gray-50"
              style={{ color: T.mid, borderColor: T.border }}
              title="Erneut parsen"
            >
              <RefreshCw className="w-3 h-3" />
              Reparse
            </button>
          )}
          <button
            onClick={onUpload}
            className="flex items-center gap-1 text-xs px-2 py-1.5 rounded-md border transition-colors hover:bg-gray-50"
            style={{ color: config.color, borderColor: `${config.color}40` }}
          >
            <Upload className="w-3 h-3" />
            {card.hasData ? "Ersetzen" : "Hochladen"}
          </button>
          {card.hasData && (
            <button
              onClick={onExpand}
              className="p-1.5 rounded-md hover:bg-gray-50 transition-colors"
              style={{ color: T.muted }}
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Parsed Data Detail ─────────────────────────────────────
function ParsedDataDetail({ fileId }: { fileId: number }) {
  const { data, isLoading } = trpc.adminBio.getParsedData.useQuery({ fileId });

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: T.olive }} />
      </div>
    );
  }

  if (!data || !data.parsedData) {
    return (
      <div className="p-4 text-center text-sm" style={{ color: T.muted }}>
        Keine geparsten Daten verfügbar
      </div>
    );
  }

  const pd = data.parsedData as any;

  // Microbiome Biomarker PDF
  if (data.fileType === "microbiome_biomarker_pdf" && pd.biomarkers) {
    return (
      <div className="p-4 space-y-3">
        <div className="p-3 rounded-lg" style={{ background: T.bg }}>
          <p className="text-xs" style={{ color: T.muted }}>Biomarker</p>
          <p className="text-lg font-bold" style={{ color: T.dark }}>{pd.totalBiomarkers}</p>
        </div>
        {pd.biomarkers.length > 0 && (
          <div className="max-h-48 overflow-auto rounded-lg border" style={{ borderColor: T.border }}>
            <table className="w-full text-xs">
              <thead>
                <tr style={{ background: T.bg }}>
                  <th className="text-left p-2 font-medium" style={{ color: T.mid }}>Biomarker</th>
                  <th className="text-left p-2 font-medium" style={{ color: T.mid }}>Wert</th>
                  <th className="text-left p-2 font-medium" style={{ color: T.mid }}>Einheit</th>
                  <th className="text-left p-2 font-medium" style={{ color: T.mid }}>Referenz</th>
                  <th className="text-left p-2 font-medium" style={{ color: T.mid }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pd.biomarkers.map((b: any, i: number) => (
                  <tr key={i} className="border-t" style={{ borderColor: T.border }}>
                    <td className="p-2 font-medium" style={{ color: T.dark }}>{b.name}</td>
                    <td className="p-2 font-mono" style={{ color: T.dark }}>{b.value}</td>
                    <td className="p-2" style={{ color: T.muted }}>{b.unit || "—"}</td>
                    <td className="p-2" style={{ color: T.muted }}>{b.referenceRange || "—"}</td>
                    <td className="p-2">
                      <Badge
                        variant="outline"
                        className={`text-[9px] py-0 ${
                          b.status === "normal"
                            ? "border-green-500/40 text-green-600 bg-green-500/10"
                            : b.status === "critical"
                              ? "border-red-500/40 text-red-600 bg-red-500/10"
                              : "border-amber-500/40 text-amber-600 bg-amber-500/10"
                        }`}
                      >
                        {b.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // Microbiome XLSX
  if (data.fileType === "microbiome_raw_xlsx" && pd.bacteria) {
    return (
      <div className="p-4 space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 rounded-lg" style={{ background: T.bg }}>
            <p className="text-xs" style={{ color: T.muted }}>Bakterien</p>
            <p className="text-lg font-bold" style={{ color: T.dark }}>{pd.totalBacteria}</p>
          </div>
          {pd.diversityIndex !== undefined && (
            <div className="p-3 rounded-lg" style={{ background: T.bg }}>
              <p className="text-xs" style={{ color: T.muted }}>Shannon-Index</p>
              <p className="text-lg font-bold" style={{ color: "#6b4a8a" }}>{pd.diversityIndex}</p>
            </div>
          )}
          {pd.topPhyla?.length > 0 && (
            <div className="p-3 rounded-lg" style={{ background: T.bg }}>
              <p className="text-xs" style={{ color: T.muted }}>Top-Phylum</p>
              <p className="text-sm font-bold" style={{ color: T.dark }}>{pd.topPhyla[0].name}</p>
              <p className="text-xs" style={{ color: T.muted }}>{pd.topPhyla[0].percentage}%</p>
            </div>
          )}
        </div>
        {pd.topPhyla?.length > 0 && (
          <div>
            <p className="text-xs font-medium mb-2" style={{ color: T.mid }}>Phyla-Verteilung:</p>
            <div className="space-y-1.5">
              {pd.topPhyla.slice(0, 8).map((p: any, i: number) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs w-28 truncate" style={{ color: T.dark }}>{p.name}</span>
                  <div className="flex-1 h-4 rounded-full overflow-hidden" style={{ background: T.bg }}>
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${Math.min(100, p.percentage)}%`,
                        background: `linear-gradient(90deg, ${T.olive}, #8a9f6e)`,
                      }}
                    />
                  </div>
                  <span className="text-xs font-mono w-12 text-right" style={{ color: T.muted }}>
                    {p.percentage}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Microbiome Report PDF
  if (data.fileType === "microbiome_report_pdf") {
    const pathogenStatusColor: Record<string, string> = {
      detected: "#ef4444",
      elevated: "#f59e0b",
      normal: "#22c55e",
      absent: "#9ca3af",
    };

    return (
      <div className="p-4 space-y-3">
        {/* Summary stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {pd.shannonIndex !== undefined && (
            <div className="p-3 rounded-lg" style={{ background: T.bg }}>
              <p className="text-xs" style={{ color: T.muted }}>Shannon-Index</p>
              <p className="text-lg font-bold" style={{ color: "#2d8a6b" }}>{pd.shannonIndex}</p>
            </div>
          )}
          {pd.dysbioseIndex !== undefined && (
            <div className="p-3 rounded-lg" style={{ background: T.bg }}>
              <p className="text-xs" style={{ color: T.muted }}>Dysbiose-Index</p>
              <p className="text-lg font-bold" style={{ color: "#8a6b4a" }}>{pd.dysbioseIndex}</p>
            </div>
          )}
          {pd.enterotype && (
            <div className="p-3 rounded-lg" style={{ background: T.bg }}>
              <p className="text-xs" style={{ color: T.muted }}>Enterotyp</p>
              <p className="text-sm font-bold" style={{ color: T.dark }}>{pd.enterotype}</p>
            </div>
          )}
          {pd.provider && (
            <div className="p-3 rounded-lg" style={{ background: T.bg }}>
              <p className="text-xs" style={{ color: T.muted }}>Anbieter</p>
              <p className="text-sm font-bold" style={{ color: T.dark }}>{pd.provider}</p>
            </div>
          )}
        </div>

        {/* Scores */}
        {pd.scores?.length > 0 && (
          <div>
            <p className="text-xs font-medium mb-2" style={{ color: T.mid }}>Scores:</p>
            <div className="space-y-2">
              {pd.scores.slice(0, 10).map((s: any, i: number) => {
                const pct = s.max > s.min ? ((s.value - s.min) / (s.max - s.min)) * 100 : 50;
                const color = s.status === "optimal" ? "#22c55e" : s.status === "suboptimal" ? "#f59e0b" : "#ef4444";
                return (
                  <div key={i}>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span style={{ color: T.dark }}>{s.name}</span>
                      <span className="font-mono" style={{ color }}>{s.value} {s.unit}</span>
                    </div>
                    <div className="relative h-3 rounded-full" style={{ background: T.bg }}>
                      <div className="absolute top-0 w-2.5 h-3 rounded-full border-2 border-white shadow-md" style={{ left: `calc(${Math.min(100, Math.max(0, pct))}% - 5px)`, background: color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pathogens */}
        {pd.pathogens?.length > 0 && (
          <div>
            <p className="text-xs font-medium mb-2" style={{ color: T.mid }}>Pathogene ({pd.pathogens.length}):</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {pd.pathogens.map((p: any, i: number) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: T.bg }}>
                  <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: pathogenStatusColor[p.status] || "#9ca3af" }} />
                  <div className="min-w-0">
                    <span className="text-xs font-medium" style={{ color: T.dark }}>{p.name}</span>
                    {p.description && <p className="text-[10px] truncate" style={{ color: T.muted }}>{p.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {pd.recommendations?.length > 0 && (
          <div>
            <p className="text-xs font-medium mb-2" style={{ color: T.mid }}>Empfehlungen:</p>
            <div className="space-y-1">
              {pd.recommendations.map((r: string, i: number) => (
                <div key={i} className="flex items-start gap-2 p-2 rounded-lg" style={{ background: T.bg }}>
                  <span className="text-xs font-bold shrink-0" style={{ color: "#2d8a6b" }}>{i + 1}.</span>
                  <span className="text-xs" style={{ color: T.dark }}>{r}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Fallback: raw JSON
  return (
    <div className="p-4">
      <pre className="text-xs p-3 rounded-lg overflow-auto max-h-48" style={{ color: T.mid, background: T.bg }}>
        {JSON.stringify(pd, null, 2)}
      </pre>
    </div>
  );
}

// ─── Gesamtanalyse Section ──────────────────────────────────
function GesamtanalyseSection({ clientId }: { clientId: string }) {
  const utils = trpc.useUtils();
  const { data: analysis, isLoading } = trpc.adminBio.getAnalysis.useQuery({ clientId });
  const [showDetails, setShowDetails] = useState(false);

  const startMutation = trpc.adminBio.startAnalysis.useMutation({
    onSuccess: (data) => {
      if (data.success) {
        toast.success("Gesamtanalyse abgeschlossen");
      } else {
        toast.error(`Analyse fehlgeschlagen: ${data.error}`);
      }
      utils.adminBio.getAnalysis.invalidate({ clientId });
      utils.adminBio.getDataOverview.invalidate({ clientId });
    },
    onError: (err) => toast.error(err.message),
  });

  const analysisData = analysis?.analysisData as any;

  return (
    <Card className="border-[#d4dcc4] bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2" style={{ color: T.saffron }}>
            <Sparkles className="w-4 h-4" />
            KI-Gesamtanalyse
          </CardTitle>
          <Button
            size="sm"
            onClick={() => startMutation.mutate({ clientId })}
            disabled={startMutation.isPending}
            className="h-7 text-xs bg-[#c8a45e] text-white hover:bg-[#b89548]"
          >
            {startMutation.isPending ? (
              <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Analysiert...</>
            ) : analysis ? (
              <><RefreshCw className="w-3 h-3 mr-1" /> Neu analysieren</>
            ) : (
              <><Sparkles className="w-3 h-3 mr-1" /> Analyse starten</>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: T.saffron }} />
          </div>
        ) : !analysis ? (
          <div className="text-center py-6">
            <Brain className="w-10 h-10 mx-auto mb-2" style={{ color: T.muted }} />
            <p className="text-sm" style={{ color: T.muted }}>
              Noch keine Gesamtanalyse durchgeführt
            </p>
            <p className="text-xs mt-1" style={{ color: T.muted }}>
              Klicke "Analyse starten" um alle Mikrobiom-Daten zusammenzuführen
            </p>
          </div>
        ) : analysis.status === "error" ? (
          <div className="p-4 rounded-lg bg-red-50 border border-red-200">
            <p className="text-sm text-red-600 font-medium">Analyse fehlgeschlagen</p>
            <p className="text-xs text-red-500 mt-1">{analysis.errorMessage}</p>
          </div>
        ) : analysis.status === "running" ? (
          <div className="flex items-center gap-3 py-4">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: T.saffron }} />
            <p className="text-sm" style={{ color: T.mid }}>Analyse läuft...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Status + Summary */}
            <div className="p-4 rounded-lg" style={{ background: T.saffronLight }}>
              <div className="flex items-center gap-2 mb-2">
                <Badge
                  variant="outline"
                  className={`text-[10px] py-0 ${
                    analysisData?.overallStatus === "gut"
                      ? "border-green-500/40 text-green-600 bg-green-500/10"
                      : analysisData?.overallStatus === "auffaellig"
                        ? "border-amber-500/40 text-amber-600 bg-amber-500/10"
                        : "border-red-500/40 text-red-600 bg-red-500/10"
                  }`}
                >
                  {analysisData?.overallStatus === "gut" ? "Gut" : analysisData?.overallStatus === "auffaellig" ? "Auffällig" : "Kritisch"}
                </Badge>
                {analysisData?.diversityScore !== null && (
                  <span className="text-xs" style={{ color: T.mid }}>
                    Diversität: {analysisData.diversityScore}/100
                  </span>
                )}
                {analysisData?.dysbioseRisk && (
                  <span className="text-xs" style={{ color: T.mid }}>
                    Dysbiose-Risiko: {analysisData.dysbioseRisk}
                  </span>
                )}
              </div>
              <p className="text-sm" style={{ color: T.dark }}>{analysis.summary}</p>
            </div>

            {/* Key Findings */}
            {analysisData?.keyFindings?.length > 0 && (
              <div>
                <p className="text-xs font-medium mb-2 flex items-center gap-1" style={{ color: T.mid }}>
                  <Activity className="w-3 h-3" /> Wichtigste Befunde
                </p>
                <div className="space-y-1.5">
                  {analysisData.keyFindings.map((f: any, i: number) => (
                    <div
                      key={i}
                      className="flex items-start gap-2 p-2 rounded-lg"
                      style={{
                        background: f.severity === "kritisch" ? "#fef2f2" : f.severity === "warnung" ? "#fffbeb" : T.bg,
                      }}
                    >
                      <Badge
                        variant="outline"
                        className={`text-[9px] py-0 shrink-0 ${
                          f.severity === "kritisch"
                            ? "border-red-500/40 text-red-600"
                            : f.severity === "warnung"
                              ? "border-amber-500/40 text-amber-600"
                              : "border-blue-500/40 text-blue-600"
                        }`}
                      >
                        {f.category}
                      </Badge>
                      <span className="text-xs" style={{ color: T.dark }}>{f.finding}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {analysisData?.recommendations?.length > 0 && (
              <div>
                <p className="text-xs font-medium mb-2 flex items-center gap-1" style={{ color: T.mid }}>
                  <BarChart3 className="w-3 h-3" /> Empfehlungen
                </p>
                <div className="space-y-1.5">
                  {analysisData.recommendations.map((r: any, i: number) => (
                    <div key={i} className="flex items-start gap-2 p-2 rounded-lg" style={{ background: T.bg }}>
                      <Badge
                        variant="outline"
                        className={`text-[9px] py-0 shrink-0 ${
                          r.priority === "hoch"
                            ? "border-red-500/40 text-red-600"
                            : r.priority === "mittel"
                              ? "border-amber-500/40 text-amber-600"
                              : "border-green-500/40 text-green-600"
                        }`}
                      >
                        {r.area}
                      </Badge>
                      <span className="text-xs" style={{ color: T.dark }}>{r.recommendation}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Phenotype Scores */}
            {analysisData?.phenotypeScores && Object.keys(analysisData.phenotypeScores).length > 0 && (
              <div>
                <p className="text-xs font-medium mb-2 flex items-center gap-1" style={{ color: T.mid }}>
                  <Brain className="w-3 h-3" /> Phänotyp-Relevanz
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {Object.entries(analysisData.phenotypeScores)
                    .sort(([, a], [, b]) => (b as number) - (a as number))
                    .map(([key, score]) => (
                      <div key={key} className="p-2 rounded-lg text-center" style={{ background: T.bg }}>
                        <p className="text-xs font-mono font-bold" style={{ color: T.dark }}>{key}</p>
                        <p className="text-lg font-bold" style={{ color: (score as number) > 60 ? T.olive : T.muted }}>
                          {score as number}
                        </p>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Detailed Sections toggle */}
            {analysisData?.detailedSections?.length > 0 && (
              <div>
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md hover:bg-gray-50 transition-colors"
                  style={{ color: T.olive }}
                >
                  {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  {showDetails ? "Details ausblenden" : "Detaillierte Analyse anzeigen"}
                </button>
                {showDetails && (
                  <div className="mt-2 space-y-3">
                    {analysisData.detailedSections.map((s: any, i: number) => (
                      <div key={i} className="p-3 rounded-lg border" style={{ borderColor: T.border }}>
                        <h5 className="text-xs font-bold mb-1" style={{ color: T.dark }}>{s.title}</h5>
                        <p className="text-xs whitespace-pre-wrap" style={{ color: T.mid }}>{s.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Metadata */}
            <div className="flex items-center gap-4 pt-2 border-t" style={{ borderColor: T.border }}>
              <span className="text-[10px]" style={{ color: T.muted }}>
                Quellen: {(analysis.sourcesUsed as any)?.totalFiles || "?"} Dateien
              </span>
              <span className="text-[10px]" style={{ color: T.muted }}>
                Erstellt: {analysis.createdAt ? new Date(analysis.createdAt).toLocaleString("de-DE") : "—"}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Client Bio Detail View ─────────────────────────────────
export function ClientBioDetail({
  clientId,
  clientName,
  onBack,
  embedded = false,
}: {
  clientId: string;
  clientName: string;
  onBack?: () => void;
  embedded?: boolean;
}) {
  const utils = trpc.useUtils();
  const { data: overview, isLoading: overviewLoading } = trpc.adminBio.getDataOverview.useQuery({ clientId });
  const { data: files, isLoading: filesLoading } = trpc.adminBio.getClientFiles.useQuery({ clientId });

  const [uploadType, setUploadType] = useState<string | null>(null);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  const reparseMutation = trpc.adminBio.reparse.useMutation({
    onSuccess: (data) => {
      if (data.success) {
        toast.success(`Reparse erfolgreich: ${data.parsedSummary}`);
      } else {
        toast.error(`Reparse fehlgeschlagen: ${data.error}`);
      }
      utils.adminBio.getDataOverview.invalidate({ clientId });
      utils.adminBio.getClientFiles.invalidate({ clientId });
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteMutation = trpc.adminBio.deleteFile.useMutation({
    onSuccess: () => {
      toast.success("Datei gelöscht");
      utils.adminBio.getDataOverview.invalidate({ clientId });
      utils.adminBio.getClientFiles.invalidate({ clientId });
    },
    onError: (err) => toast.error(err.message),
  });

  const handleRefresh = () => {
    utils.adminBio.getDataOverview.invalidate({ clientId });
    utils.adminBio.getClientFiles.invalidate({ clientId });
    utils.adminBio.getAnalysis.invalidate({ clientId });
  };

  if (overviewLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: T.olive }} />
      </div>
    );
  }

  // Filter files to only show Mikrobiom types
  const mikrobiomFiles = files?.filter(
    (f) => f.fileType === "microbiome_biomarker_pdf" || f.fileType === "microbiome_raw_xlsx" || f.fileType === "microbiome_report_pdf"
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      {!embedded && (
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={onBack}
            className="border-[#d4dcc4] text-[#5a6b4a] hover:text-[#6b7f4e] h-8 w-8 shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h3 className="text-lg font-bold" style={{ color: T.dark, fontFamily: "'Playfair Display', serif" }}>
              {clientName}
            </h3>
            <p className="text-xs" style={{ color: T.muted }}>Mikrobiom-Daten · {clientId}</p>
          </div>
          <Button
            size="sm"
            onClick={() => setBulkUploadOpen(true)}
            className="bg-[#6b7f4e] text-white hover:bg-[#5a6e3f]"
          >
            <Upload className="w-4 h-4 mr-2" />
            Dateien hochladen
          </Button>
        </div>
      )}
      {embedded && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Microscope className="w-4 h-4" style={{ color: T.olive }} />
            <span className="text-sm font-medium" style={{ color: T.olive }}>Mikrobiom-Daten</span>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setBulkUploadOpen(true)}
            className="border-[#6b7f4e]/30 text-[#6b7f4e] hover:bg-[#6b7f4e]/10"
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            Hochladen
          </Button>
        </div>
      )}

      {/* Geparste Daten Übersicht */}
      <Card className="border-[#d4dcc4] bg-white">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2" style={{ color: T.olive }}>
              <Microscope className="w-4 h-4" />
              Geparste Daten Übersicht
            </CardTitle>
            <div className="flex items-center gap-2">
              {overview && (
                <Badge
                  variant="outline"
                  className={`text-[10px] py-0 ${
                    overview.parsedSources === overview.totalSources
                      ? "border-green-500/40 text-green-600 bg-green-500/10"
                      : overview.parsedSources > 0
                        ? "border-amber-500/40 text-amber-600 bg-amber-500/10"
                        : "border-gray-500/40 text-gray-600 bg-gray-500/10"
                  }`}
                >
                  {overview.parsedSources === overview.totalSources
                    ? "Vollständig"
                    : overview.parsedSources > 0
                      ? "Teilweise"
                      : "Keine Daten"}
                </Badge>
              )}
              <span className="text-xs" style={{ color: T.muted }}>
                {overview?.parsedSources || 0}/{overview?.totalSources || 3} Datenquellen geparst
              </span>
              <span className="text-xs" style={{ color: T.muted }}>
                · {overview?.totalFiles || 0} Dateien insgesamt
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {overview?.cards.map((card) => (
            <div key={card.fileType}>
              <DataSourceCard
                card={card}
                onUpload={() => setUploadType(card.fileType)}
                onReparse={() => card.latestFileId && reparseMutation.mutate({ fileId: card.latestFileId })}
                onExpand={() => setExpandedCard(expandedCard === card.fileType ? null : card.fileType)}
                expanded={expandedCard === card.fileType}
              />
              {expandedCard === card.fileType && card.latestFileId && (
                <div className="mt-1 ml-4 border-l-2 pl-4" style={{ borderColor: T.border }}>
                  <ParsedDataDetail fileId={card.latestFileId} />
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* KI-Gesamtanalyse */}
      <GesamtanalyseSection clientId={clientId} />

      {/* Alle Dateien */}
      <Card className="border-[#d4dcc4] bg-white">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2" style={{ color: T.olive }}>
            <FileText className="w-4 h-4" />
            Alle Dateien ({mikrobiomFiles?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filesLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: T.olive }} />
            </div>
          ) : !mikrobiomFiles || mikrobiomFiles.length === 0 ? (
            <p className="text-sm text-center py-4" style={{ color: T.muted }}>
              Noch keine Dateien hochgeladen
            </p>
          ) : (
            <div className="space-y-2">
              {mikrobiomFiles.map((file) => {
                const config = FILE_TYPE_CONFIG[file.fileType];
                return (
                  <div
                    key={file.id}
                    className="flex items-center gap-3 p-3 rounded-lg border"
                    style={{ borderColor: T.border, background: T.bg }}
                  >
                    <Badge
                      variant="outline"
                      className="text-[9px] py-0.5 px-1.5 shrink-0"
                      style={{ color: config?.color, borderColor: `${config?.color}40`, background: `${config?.color}10` }}
                    >
                      {config?.shortLabel || file.fileType}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate" style={{ color: T.dark }}>{file.fileName}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <StatusBadge status={file.status} />
                        {file.parsedSummary && (
                          <span className="text-[10px]" style={{ color: T.muted }}>{file.parsedSummary}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => reparseMutation.mutate({ fileId: file.id })}
                        disabled={reparseMutation.isPending}
                        className="p-1.5 rounded-md hover:bg-white transition-colors"
                        title="Reparse"
                      >
                        <RefreshCw className="w-3.5 h-3.5" style={{ color: T.muted }} />
                      </button>
                      {file.fileUrl && (
                        <a
                          href={file.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-md hover:bg-white transition-colors"
                          title="Herunterladen"
                        >
                          <Download className="w-3.5 h-3.5" style={{ color: T.muted }} />
                        </a>
                      )}
                      <button
                        onClick={() => {
                          if (confirm("Datei wirklich löschen?")) {
                            deleteMutation.mutate({ fileId: file.id });
                          }
                        }}
                        className="p-1.5 rounded-md hover:bg-red-50 transition-colors"
                        title="Löschen"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Upload Dialog */}
      <BulkUploadDialog
        open={bulkUploadOpen}
        onOpenChange={setBulkUploadOpen}
        clientId={clientId}
        onSuccess={handleRefresh}
      />

      {/* Single Upload Dialog (for replacing specific type) */}
      {uploadType && (
        <SingleUploadDialog
          open={!!uploadType}
          onOpenChange={(open) => !open && setUploadType(null)}
          clientId={clientId}
          fileType={uploadType}
          onSuccess={handleRefresh}
        />
      )}
    </div>
  );
}

// ─── Main Admin Bio Tab ──────────────────────────────────────
export default function AdminBioTab() {
  const { data: clients, isLoading } = trpc.adminBio.clientsOverview.useQuery();
  const [selectedClient, setSelectedClient] = useState<{
    clientId: string;
    name: string;
  } | null>(null);
  const [search, setSearch] = useState("");

  if (selectedClient) {
    return (
      <ClientBioDetail
        clientId={selectedClient.clientId}
        clientName={selectedClient.name}
        onBack={() => setSelectedClient(null)}
      />
    );
  }

  const filtered = clients?.filter((c) => {
    const q = search.toLowerCase();
    return (
      !q ||
      c.firstName.toLowerCase().includes(q) ||
      c.lastName.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      c.clientId.toLowerCase().includes(q)
    );
  });

  const sorted = filtered?.sort((a, b) => b.bioFileCount - a.bioFileCount);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold" style={{ color: T.dark, fontFamily: "'Playfair Display', serif" }}>
          Mikrobiom-Daten
        </h3>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: T.muted }} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Kunde suchen..."
            className="pl-9 bg-[#f8f8f8] border-[#d4dcc4] text-[#2d3a1e] text-sm"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: T.olive }} />
        </div>
      ) : !sorted || sorted.length === 0 ? (
        <p className="text-center py-8" style={{ color: T.muted }}>
          Keine Kunden gefunden
        </p>
      ) : (
        <div className="space-y-2">
          {sorted.map((c) => (
            <div
              key={c.clientId}
              onClick={() =>
                setSelectedClient({
                  clientId: c.clientId,
                  name: `${c.firstName} ${c.lastName}`,
                })
              }
              className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors hover:border-[#6b7f4e]/40"
              style={{ borderColor: T.border, background: T.white }}
            >
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                style={{ background: T.oliveLight, color: T.olive }}
              >
                {c.firstName[0]}
                {c.lastName[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: T.dark }}>
                  {c.firstName} {c.lastName}
                </p>
                <p className="text-xs truncate" style={{ color: T.muted }}>
                  {c.email}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge
                  variant="outline"
                  className={`text-[10px] py-0 ${
                    c.packageType === "premium"
                      ? "border-[#6b7f4e]/40 text-[#6b7f4e] bg-[#6b7f4e]/10"
                      : "border-[#7d7060]/40 text-[#5a6b4a]"
                  }`}
                >
                  {c.packageType}
                </Badge>
                {c.bioFileCount > 0 ? (
                  <Badge variant="outline" className="text-[10px] py-0 border-[#6b4a8a]/40 text-[#6b4a8a] bg-[#6b4a8a]/10">
                    <Microscope className="w-3 h-3 mr-1" />
                    {c.bioFileCount} Dateien
                  </Badge>
                ) : (
                  <span className="text-[10px]" style={{ color: T.muted }}>
                    Keine Bio-Daten
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
